import React, { useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Header from "./components/Header";
import Sidebar from "./components/Sidebar";
import ProjectList from "./components/ProjectList";
import ProjectBoard from "./components/ProjectBoard";
import AdminPanel from "./components/AdminPanel";
import ArchivedProjects from "./components/ArchivedProjects";
import ManageProjects from "./components/ManageProjects";
import Notifications from "./components/Notifications";
import Settings from "./components/Settings";
import AuthPage from "./components/AuthPage";
import UserManagement from "./components/UserManagement";
import InvitationPage from "./components/InvitationPage";
import { NotificationProvider } from "./contexts/NotificationContext";
import { UserProvider } from "./contexts/UserContext";
import { ProjectProvider } from "./contexts/ProjectContext";
import { SocketProvider } from "./contexts/SocketContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { CardImageVisibilityProvider } from "./contexts/CardImageVisibilityContext";
import { BookmarkProvider } from "./contexts/BookmarkContext";
import { ChatProvider } from "./contexts/ChatContext";
import { TeamChatPanel } from "./components/Chat/TeamChatPanel";
import { TeamChatLauncher } from "./components/Chat/TeamChatLauncher";
import { useUser } from "./contexts/UserContext";
import ResetPassword from "./components/ResetPassword";
import LandingPage from "./pages/LandingPage";
import FeaturesPage from "./pages/FeaturesPage";
import WhyTeamBoard from "./pages/WhyTeamBoard";
import PricingPage from "./pages/PricingPage";
import TestimonialsPage from "./pages/TestimonialsPage";
import FAQPage from "./pages/FAQPage";
import ContactUsPage from "./pages/ContactUsPage";
import AboutUsPage from "./pages/AboutUsPage";
import CareersPage from "./pages/CareersPage";
import IntegrationsPage from "./pages/IntegrationsPage";
import ChangelogPage from "./pages/ChangelogPage";
import SecurityPage from "./pages/SecurityPage";
import BlogPage from "./pages/BlogPage";
import HelpCenterPage from "./pages/HelpCenterPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsAndConditionsPage from "./pages/TermsAndConditionsPage";
import ScrollToTop from "./components/ScrollToTop";
import { features } from "./config/features";

// Root of the app subdomain: send users into the app, not the marketing page.
// Signed out -> /login (AuthPage); signed in -> /dashboard (ProjectList).
function RootRedirect() {
  const { isAuthenticated } = useUser();
  return <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />;
}

function AppContent() {
  const { isAuthenticated, loading } = useUser();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <Routes>
        {!isAuthenticated ? (
          <>
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="*" element={<AuthPage />} />
          </>
        ) : (
          <Route
            path="*"
            element={
              <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex flex-col h-screen overflow-hidden">
                <Header
                  onMenuClick={() => setSidebarOpen(!sidebarOpen)}
                  onToggleSidebar={() =>
                    setSidebarCollapsed(!sidebarCollapsed)
                  }
                  sidebarCollapsed={sidebarCollapsed}
                />

                <div className="flex flex-1 overflow-hidden">
                  <Sidebar
                    isOpen={sidebarOpen}
                    isCollapsed={sidebarCollapsed}
                    onClose={() => setSidebarOpen(false)}
                    onToggleCollapse={() =>
                      setSidebarCollapsed(!sidebarCollapsed)
                    }
                  />

                  <main className="flex-1 p-4 overflow-auto transition-all duration-300 bg-zinc-50/90 dark:bg-slate-900/90">
                    <Routes>
                      <Route path="/" element={<ProjectList />} />
                      <Route path="/dashboard" element={<ProjectList />} />
                      <Route path="/admin" element={<AdminPanel />} />
                      <Route path="/archived-projects" element={<ArchivedProjects />} />
                      <Route path="/manage-projects" element={<ManageProjects />} />
                      <Route path="/users" element={<UserManagement />} />
                      <Route
                        path="/notifications"
                        element={<Notifications />}
                      />
                      <Route path="/settings" element={<Settings />} />
                      <Route path="/project/:id" element={<ProjectBoard />} />
                      <Route
                        path="/project/:id/edit"
                        element={<ProjectBoard />}
                      />
                      <Route
                        path="/project/:projectId/edit"
                        element={<ProjectBoard />}
                      />
                      <Route
                        path="/project/:projectId/card/:cardId"
                        element={<ProjectBoard />}
                      />
                    </Routes>
                  </main>
                </div>

                {/* Team Chat Floating Components */}
                <TeamChatPanel />
                <TeamChatLauncher />
              </div>
            }
          />
        )}
      </Routes>
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <UserProvider>
          <Router>
            <ScrollToTop />
            <Routes>
              <Route
                path="/"
                element={
                  features.landingPage ? <LandingPage /> : <RootRedirect />
                }
              />
              <Route
                path="/landing"
                element={
                  features.landingPage ? <LandingPage /> : <RootRedirect />
                }
              />
              <Route path="/features" element={<FeaturesPage />} />
              <Route path="/why-teamboard" element={<WhyTeamBoard />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/about-us" element={<AboutUsPage />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/integrations" element={<IntegrationsPage />} />
              <Route path="/changelog" element={<ChangelogPage />} />
              <Route path="/security" element={<SecurityPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/help" element={<HelpCenterPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsAndConditionsPage />} />
              <Route path="/testimonials" element={<TestimonialsPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/contact" element={<ContactUsPage />} />
              <Route path="/invite/:token" element={<InvitationPage />} />
              <Route
                path="*"
                element={
                  <SocketProvider>
                    <ProjectProvider>
                      <NotificationProvider>
                        <CardImageVisibilityProvider>
                          <BookmarkProvider>
                            <ChatProvider>
                              <AppContent />
                            </ChatProvider>
                          </BookmarkProvider>
                        </CardImageVisibilityProvider>
                      </NotificationProvider>
                    </ProjectProvider>
                  </SocketProvider>
                }
              />
            </Routes>
          </Router>
        </UserProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
